import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Truck, Minus, Plus, ArrowRight } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { ShopifyProduct } from "@/lib/shopify";

interface ProductQuickPreviewProps {
  product: ShopifyProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

const getTreeSizeSubtitle = (title: string) => {
  if (title.toLowerCase().includes('5') || title.toLowerCase().includes('family')) {
    return '5-Pack — Trees 8ft+';
  }
  if (title.toLowerCase().includes('2')) {
    return '2-Pack — Trees 6-8ft';
  }
  return '1 Bottle — Trees Under 6ft';
};

export const ProductQuickPreview = ({ product, isOpen, onClose }: ProductQuickPreviewProps) => {
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore(state => state.addItem);

  if (!product) return null;

  const variant = product.node.variants.edges[0]?.node;
  const price = variant ? parseFloat(variant.price.amount) : 0;
  const treeSizeSubtitle = getTreeSizeSubtitle(product.node.title);
  const description = product.node.description?.slice(0, 150) + (product.node.description?.length > 150 ? '...' : '');

  const handleAddToCart = () => {
    if (!variant) return;
    
    const cartItem = {
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity,
      selectedOptions: variant.selectedOptions
    };
    addItem(cartItem);
    setQuantity(1);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setQuantity(1);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Product Image */}
          <div className="h-48 md:h-auto md:aspect-square bg-background flex items-center justify-center p-4 md:p-6">
            {product.node.images.edges[0]?.node ? (
              <img 
                src={`${product.node.images.edges[0].node.url}&width=400`} 
                alt={product.node.title} 
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center">
                <span className="text-muted-foreground">No image</span>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="p-4 md:p-6 flex flex-col justify-between">
            <div className="space-y-2 md:space-y-3">
              <div>
                <h3 className="font-cormorant text-xl md:text-2xl font-bold text-foreground">
                  Christmas Tree Food
                </h3>
                <p className="text-sm text-muted-foreground font-inter">
                  {treeSizeSubtitle}
                </p>
              </div>

              <div>
                <p className="text-xs text-primary font-inter flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5" />
                  FAST & FREE SHIPPING
                </p>
                <span className="text-xl md:text-2xl font-inter text-foreground font-semibold">
                  ${price.toFixed(2)}
                </span>
              </div>

              {description && (
                <p className="hidden sm:block text-sm text-muted-foreground font-inter leading-relaxed">
                  {description}
                </p>
              )}
            </div>

            <div className="space-y-2 md:space-y-3 mt-3 md:mt-4">
              {/* Quantity Selector */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-inter text-muted-foreground">Qty:</span>
                <div className="flex items-center border border-border rounded-md">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-8 text-center text-sm font-inter">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-inter font-semibold h-11" 
                onClick={handleAddToCart}
                disabled={!variant?.availableForSale}
              >
                {variant?.availableForSale ? 'Add to Cart' : 'Out of Stock'}
              </Button>

              {/* View Details Link */}
              <Link 
                to={`/product/${product.node.handle}`}
                className="w-full"
                onClick={onClose}
              >
                <Button 
                  variant="ghost" 
                  className="w-full font-inter text-muted-foreground hover:text-foreground"
                >
                  View Details
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
